"use client";

import React, { useRef, useEffect, useState } from "react";
import Image from "@/components/OptimizedImage";

interface VideoHeroProps {
  title?: string;
  tagline?: string;
  video?: string;
  videoPortrait?: string;
  poster?: string;
  posterAlt?: string;
}

const VideoHero = ({ title, tagline, video, videoPortrait, poster, posterAlt }: VideoHeroProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [isPortrait, setIsPortrait] = useState(false);

  // Detect portrait/landscape orientation
  useEffect(() => {
    if (!videoPortrait) return;
    const mql = window.matchMedia("(orientation: portrait)");
    setIsPortrait(mql.matches);
    const handler = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [videoPortrait]);

  const activeVideo = videoPortrait && isPortrait ? videoPortrait : video;

  useEffect(() => {
    if (!activeVideo) return;
    // Reset state when video source changes
    setVideoLoaded(false);
    setVideoSrc(null);
    // Defer video loading until main thread is idle
    const loadVideo = () => {
      setVideoSrc(activeVideo);
    };

    // Use requestIdleCallback if available, otherwise use setTimeout
    if ("requestIdleCallback" in window) {
      const idleId = requestIdleCallback(loadVideo, { timeout: 2000 });
      return () => cancelIdleCallback(idleId);
    } else {
      // Fallback: load after a short delay to allow LCP to complete
      const timeoutId = setTimeout(loadVideo, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [activeVideo]);

  // Start playing once video data is loaded
  useEffect(() => {
    if (videoSrc && videoRef.current) {
      const video = videoRef.current;

      const handleCanPlay = () => {
        video.play().catch(() => {
          // Autoplay may be blocked, that's okay
        });
        setVideoLoaded(true);
      };

      video.addEventListener("canplay", handleCanPlay);
      return () => video.removeEventListener("canplay", handleCanPlay);
    }
  }, [videoSrc]);

  return (
    <div className="relative w-full overflow-hidden" style={{ height: "116lvh" }}>
      {/* Poster image */}
      <Image
        src={`${poster}.jpg`}
        alt={posterAlt || ""}
        fill
        sizes="100vw"
        priority
        className={`object-cover transition-opacity duration-500 ${videoLoaded ? "opacity-0" : "opacity-100"}`}
      />

      {/* Video - loaded after idle, fades in when ready */}
      {videoSrc && (
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${videoLoaded ? "opacity-100" : "opacity-0"}`}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}

      {/* Hero Content */}
      <div className="relative z-10 flex h-full items-center justify-center">
        <div className="text-center text-white p-8">
          {title && (
            <h1 className="text-5xl font-extrabold md:text-7xl">
              {title}
            </h1>
          )}
          {tagline && <p className="mt-4 text-4xl">{tagline}</p>}
        </div>
      </div>
    </div>
  );
};

export default VideoHero;
